import { useCallback } from "react";

import amplitude from "amplitude-js";
import analytics from "react-ga4";

// you can find the ga ID through the source code of any site that uses Google Analytics
const GA_MEASUREMENT_ID = "G-EC4M2KFX5W";

analytics.initialize(GA_MEASUREMENT_ID);

interface HandlerRequiredParam {
  name: string;
  description: string;
  uiElement: string;
}

interface HandlerParam extends HandlerRequiredParam {
  [key: string]: string | Date | number;
}

/**
 * Custom Hook to log an analytics event. Handles sending values to event.
 * Simply provide the other parts of the `EventData` object.
 *
 * @example
 *
 * import { useHandleAnalyticsEvent } from "/utils/analytics";
 *
 * function ExampleComponent() {
 *   // Obtain the logEvent function via hook
 *   const logEvent = useHandleAnalyticsEvent();
 *
 *   // Use logEvent function with your eventData
 *  const handleLogEvent = () => logEvent({
 *    name: "Click Event",
 *    description: "Button clicked",
 *    uiElement: "Button",
 *  });
 *
 *   return <button onClick={handleLogEvent}>Click Me!</button>;
 * }
 */
export const useHandleAnalyticsEvent = () => {
  return useCallback(
    ({ name, description, uiElement, ...rest }: HandlerParam) => {
      logAnalyticsEvent({
        name,
        description,
        ui_element: uiElement,
        time_stamp: new Date(),
        ...rest,
      });
    },
    []
  );
};

export const logAnalyticsEvent = (event: AnalyticsEvent) => {
  analytics.event(event.name, event);
};

/** Snake case is the naming convention used in Google Analytics 4 (GA4) for events and parameters **/
interface AnalyticsEventBase {
  /** Name of the event */
  name: string;
  /** Description of the event */
  description: string;
  /** Name of the UI element that triggered the event, e.g., button, list, text box, etc. */
  ui_element: string;
}

interface AnalyticsEvent extends AnalyticsEventBase {
  [key: string]: string | Date | number;
}

interface LogEventProperties {
  name: string;
  category?: string;
  eventProperties?: {
    [key: string]: string | null;
  };
}

/**
 * @deprecated This function is deprecated and will be removed in future versions.
 * Please use the new analytics tracking method instead.
 */
export const logEvent = ({
  name,
  category = "Tours",
  eventProperties = {},
}: LogEventProperties) => {
  amplitude
    .getInstance()
    .logEvent(name, { Event_Category: category, ...eventProperties });
};
